import {
  CheckboxProps as MUICheckboxProps,
  TypographyProps,
} from '@mui/material';
import { useCallback } from 'react';
import {
  Control,
  Controller,
  FieldValues,
  Path,
  RegisterOptions,
} from 'react-hook-form';

import { getDataTestId } from '@/utils/browser';
import { getObjectFieldByPath } from '@/utils/string';

import { Icon } from '../Icon/Icon';

import {
  CheckboxContainer,
  StyledCheckbox,
  StyledCheckboxFormControlLabel,
} from './Form.styled';

type CheckboxProps<FormPayload extends FieldValues> = {
  control?: Control<FormPayload>;
  label?: string;
  labelProps?: TypographyProps;
  name?: Path<FormPayload>;
  rules?: Omit<
    RegisterOptions<FormPayload, Path<FormPayload>>,
    'valueAsNumber' | 'valueAsDate' | 'setValueAs' | 'disabled'
  >;
} & Omit<MUICheckboxProps, 'variant' | 'name'>;

export function Checkbox<FormPayload extends FieldValues>({
  control,
  name,
  rules,
  label,
  labelProps,
  value,
  onChange,
  onBlur,
  ...props
}: CheckboxProps<FormPayload>) {
  const render = useCallback(
    (
      value: MUICheckboxProps['value'],
      onChange: MUICheckboxProps['onChange'],
      onBlur: MUICheckboxProps['onBlur'],
      defaultValue?: MUICheckboxProps['value'],
    ) => (
      <StyledCheckboxFormControlLabel
        control={
          <StyledCheckbox
            {...getDataTestId(`${label}-checkbox`)}
            icon={<CheckboxContainer />}
            checkedIcon={
              <CheckboxContainer checked>
                <Icon name="check-mini" />
              </CheckboxContainer>
            }
            checked={
              typeof value === 'boolean'
                ? value
                : (defaultValue as boolean) || false
            }
            onChange={onChange}
            onBlur={onBlur}
            {...props}
          />
        }
        label={label}
        slotProps={{
          typography: {
            variant: 'body2',
            ...labelProps,
          },
        }}
      />
    ),
    [label, labelProps, props],
  );

  if (control && name) {
    return (
      <Controller
        rules={rules}
        control={control}
        name={name}
        render={({ field: { onChange, onBlur, value }, formState }) =>
          render(
            value,
            onChange,
            onBlur,
            getObjectFieldByPath(formState.defaultValues, name),
          )
        }
      />
    );
  }

  return render(value, onChange, onBlur);
}
